// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { AlertTriangle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isLongRunning, type SftpManualRun, UNFINISHED_STATUSES } from "@/api/hooks/use-sftp-manual-runs";
import { DateTimeValue, DateValue } from "@/components/shared/datetime-value";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const STATUS_COLORS: Record<SftpManualRun["status"], string> = {
  pending: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
  running: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
  completed: "bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900",
  failed: "bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900",
};

/** How many finished runs are kept as context under the outstanding ones. */
const MAX_FINISHED = 10;

interface ManualSftpRunListProps {
  runs: SftpManualRun[];
  /**
   * Set on a store page. Rows naming this store are the store's own; rows with no store are
   * chain-wide replays that cover it as a side effect, and are labelled as such.
   */
  scopedStoreId?: number;
}

export function ManualSftpRunList({ runs, scopedStoreId }: ManualSftpRunListProps) {
  const { t } = useTranslation("back-office");

  const { outstanding, finished } = useMemo(() => {
    const isUnfinished = (run: SftpManualRun) => UNFINISHED_STATUSES.includes(run.status);
    return {
      outstanding: runs.filter(isUnfinished),
      finished: runs.filter((run) => !isUnfinished(run)).slice(0, MAX_FINISHED),
    };
  }, [runs]);

  if (runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("imports.manualSftp.queuedEmpty")}</p>;
  }

  return (
    <div className="space-y-3">
      {outstanding.length > 0 && (
        <ul className="space-y-1.5">
          {outstanding.map((run) => (
            <ManualSftpRunRow key={run.sftpImportManualRunId} run={run} scopedStoreId={scopedStoreId} />
          ))}
        </ul>
      )}
      {outstanding.length === 0 && (
        <p className="text-sm text-muted-foreground">{t("imports.manualSftp.queuedNoneOutstanding")}</p>
      )}
      {finished.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-xs uppercase tracking-wider text-muted-foreground">
            {t("imports.manualSftp.recentlyFinished", { count: finished.length })}
          </summary>
          <ul className="space-y-1.5 pt-2">
            {finished.map((run) => (
              <ManualSftpRunRow key={run.sftpImportManualRunId} run={run} scopedStoreId={scopedStoreId} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ManualSftpRunRow({ run, scopedStoreId }: { run: SftpManualRun; scopedStoreId?: number }) {
  const { t } = useTranslation("back-office");
  const longRunning = isLongRunning(run);
  // On a store page, a row with no store is a chain-wide replay that happens to cover this store.
  const coversByChain = scopedStoreId != null && run.storeId == null;

  return (
    <li className="rounded-md border p-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={`font-normal text-xs ${STATUS_COLORS[run.status]}`}>
          {t(`imports.manualSftp.status.${run.status}`)}
        </Badge>
        <span className="font-mono text-xs">{run.locationName}</span>
        <span className="text-muted-foreground">
          <DateValue value={run.importDate} />
        </span>
        {run.storeName ? (
          <Badge variant="outline" className="font-normal text-xs">
            {run.storeName}
          </Badge>
        ) : (
          <Badge variant="outline" className="font-normal text-xs">
            {coversByChain ? t("imports.manualSftp.chainWideCovers") : t("imports.manualSftp.chainWide")}
          </Badge>
        )}
        {longRunning && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                {t("imports.manualSftp.longRunning")}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{t("imports.manualSftp.longRunningHint")}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="pt-1 text-xs text-muted-foreground">
        {run.requestedBy && <span>{run.requestedBy}</span>}
        {run.requestedBy && run.createdAt && <span> · </span>}
        {run.createdAt && <DateTimeValue value={run.createdAt} />}
        {run.reason && <span> · {run.reason}</span>}
      </div>
      {run.errorMessage && <p className="pt-1 font-mono text-xs text-destructive break-all">{run.errorMessage}</p>}
    </li>
  );
}
